/**
 * Every call to the planner API, and the shapes it answers with. One place, so
 * no screen holds a fetch.
 *
 * The shapes are read from `api/app/schemas/runs.py` (28 September 2026; the
 * week and `today` on 1 October 2026) and
 * checked against what the API serialises over its own test fixtures, not
 * typed from memory. Names are as serialised, which is not always as declared
 * in Python: `Trip.return_` goes over the wire as `return`, and
 * `Findings.date_from`/`date_to` as `from`/`to` (both `serialization_alias`).
 * Every time is ISO 8601 with its offset, in the planner's local zone, which
 * the day carries as `time_zone`; every date is `YYYY-MM-DD`.
 */
import {
  BrowserAuthErrorCodes,
  InteractionRequiredAuthErrorCodes,
} from "@azure/msal-browser";
import { ApiError, readError } from "@depicomnlbv/platform-client";

import { session } from "../auth/session";

export interface Run {
  run_id: number;
  label: string;
  planner_version: string;
  horizon_start: string;
  horizon_end: string;
  locked_through: string | null;
  status: string;
  started_at: string;
  completed_at: string | null;
  /** Routes written under this run. The API's count, never a list's length. */
  route_count: number;
  first_route_date: string | null;
  last_route_date: string | null;
}

export interface RunList {
  /** The planner's local date (planner_config.local_time_zone), from the API,
   *  so the landing page never takes today from the browser's zone. */
  today: string;
  runs: Run[];
}

export interface Point {
  lat: number;
  lon: number;
}

export interface Vehicle {
  cab_unit_id: number | null;
  registration: string | null;
  trailer_registration: string | null;
  vehicle_type: string | null;
  capacity_litres: number | null;
  external: boolean | null;
  /** Where the vehicle is based: for an external haulier its yard, where its
   *  first trip of the day starts and its drive home ends (decision P3). Null
   *  when the unit records no base, as our own trucks do. */
  base: Point | null;
}

export interface Stop {
  stop_seq: number;
  farmer_id: number;
  farm_name: string | null;
  location: Point | null;
  arrival: string;
  departure: string;
  service_min: number | null;
  window_earliest: string | null;
  window_latest: string | null;
  window_ok: boolean;
}

export interface Leg {
  leg_seq: number;
  /** Where the leg starts and ends; null is the trip's base (the depot, or
   *  the haulier's yard). */
  from_stop_seq: number | null;
  to_stop_seq: number | null;
  distance_m: number | null;
  travel_s: number | null;
  /** [[lat, lon], ...]; null when the leg carries no geometry. */
  geometry: number[][] | null;
  depart_at: string | null;
  source: string | null;
  routed_at: string | null;
}

export interface Trip {
  route_id: number;
  /**
   * The planner's shift type (`AM`, `PM`, ...). Sent by the API and never
   * shown: there are no fixed shifts (spec section 5, 27 September 2026), so
   * a trip is named "Trip n" per vehicle per day, by departure.
   */
  shift: string | null;
  trip_seq: number | null;
  vehicle: Vehicle;
  driver_slot: number | null;
  departure: string | null;
  /** `Trip.return_` in Python, serialised under this name. */
  return: string | null;
  duration_h: number | null;
  litres: number | null;
  distance_km: number | null;
  solver_method: string | null;
  stops: Stop[];
  /** Empty when the trip has not been routed. */
  legs: Leg[];
}

export interface Day {
  run_id: number;
  date: string;
  /** The IANA zone every time on this day is in, e.g. Europe/London. */
  time_zone: string;
  depot: Point | null;
  /** False while transport.planned_route_legs does not exist. */
  legs_available: boolean;
  trips: Trip[];
}

export interface FindingSource {
  name: string;
  status: "computed" | "not_available";
  detail: string | null;
}

export interface Finding {
  source: string;
  /** The validator's code ("R3"), for a program. A screen shows `rule_name`. */
  rule: string;
  /** The rule in words, from the API (`api/app/services/finding_words.py`). */
  rule_name: string;
  severity: "hard" | "warn";
  /** In words: no date or shift prefix, no ISO date, no AM or PM trip. */
  text: string;
  /** The farm the finding names, when it names one. */
  farm: string | null;
  /** Set only on a finding about one trip. */
  route_id: number | null;
  /** Null on a finding about a farm's gap between collections or the whole run. */
  date: string | null;
}

export interface Findings {
  run_id: number;
  /** `Findings.date_from` in Python, serialised under this name. */
  from: string;
  /** `Findings.date_to` in Python, serialised under this name. */
  to: string;
  sources: FindingSource[];
  /** The API's counts over the findings it returned. Never recomputed here. */
  hard: number;
  warn: number;
  findings: Finding[];
}

export interface FindingCount {
  hard: number;
  warn: number;
}

/** One own driver's day: from the pre-trip check before the first departure to
 *  the last return, as the validator measures it. The haulier has none. */
export interface Duty {
  driver_slot: number;
  start: string;
  end: string;
  hours: number;
  /** Against the aim and the limit in `Week`, compared by the API. */
  status: "within" | "above_aim" | "above_limit";
  route_ids: number[];
}

export interface WeekDay {
  date: string;
  /** As the day endpoint sends them, with no legs: the week draws no map. */
  trips: Trip[];
  duties: Duty[];
  /** Findings dated on this day, counted by the API. */
  findings: FindingCount;
}

export interface Week {
  run_id: number;
  /** The Monday that names the week. */
  monday: string;
  time_zone: string;
  /** planner_config's driver_day_soft_max_hours and driver_day_hard_max_hours. */
  duty_aim_hours: number;
  duty_limit_hours: number;
  pre_trip_min: number;
  /** Monday to Sunday, cut to the run's horizon. */
  days: WeekDay[];
  /** Findings with no date, which belong to no day. */
  undated_findings: FindingCount;
  finding_sources: FindingSource[];
}

/**
 * A refusal from the API, with the whole of its envelope.
 *
 * The API answers every refusal as `{detail, code, field}` (coding standards
 * 11.3, `api/app/core/errors.py`), and the code is what a screen branches on:
 * a run that does not exist and a day outside a run's horizon are both 404s
 * and need different sentences. `@depicomnlbv/platform-client` 0.1.0 reads
 * `detail` and drops `code` and `field` (its `readError` keeps only the
 * sentence and 422 field errors; read from its dist on 28 September 2026, and
 * 0.1.0 is its only version). So the error is the package's own `ApiError`,
 * read by the package's own `readError`, with the two missing fields added
 * from the same body. The day the package carries them, this subclass and
 * `request` below go, and `createClient` does the whole job.
 */
export class PlannerApiError extends ApiError {
  readonly code: string | null;
  readonly field: string | null;
  /** On a 401: whether this refusal sent the reader to the sign-in page. False
   *  means the API refused a sign-in that already happened, and a screen says
   *  so instead of pretending another redirect would help. */
  readonly signingIn: boolean;

  constructor(base: ApiError, code: string | null, field: string | null, signingIn = false) {
    super({
      status: base.status,
      path: base.path,
      detail: base.detail,
      fieldErrors: base.fieldErrors,
    });
    this.name = "PlannerApiError";
    this.code = code;
    this.field = field;
    this.signingIn = signingIn;
  }
}

/** The envelope's code, or null for anything that is not a refusal with one. */
export function errorCode(error: unknown): string | null {
  return error instanceof PlannerApiError ? error.code : null;
}

/**
 * Whether this bundle answers from fixture data instead of the API.
 *
 * Both halves are needed and the order matters. `import.meta.env.DEV` is
 * replaced by the literal `false` in a production build, so the whole
 * expression is `false` there, the branch in `request` is dead code, and the
 * bundler drops the dynamic import of `./fixtures` with it: no fixture row can
 * reach a deployment, whatever VITE_FIXTURES is set to where it is built.
 * `npm run check:no-fixtures` proves that rather than trusting this comment:
 * it builds for production with VITE_FIXTURES=1 and reads the bundle.
 */
export const fixtureMode: boolean =
  import.meta.env.DEV && import.meta.env.VITE_FIXTURES === "1";

type Query = Record<string, string | number | undefined>;

async function envelopeFields(response: Response): Promise<[string | null, string | null]> {
  try {
    const body: unknown = await response.json();
    if (body && typeof body === "object") {
      const { code, field } = body as { code?: unknown; field?: unknown };
      return [
        typeof code === "string" ? code : null,
        typeof field === "string" ? field : null,
      ];
    }
  } catch {
    // Not JSON: a proxy's error page, for instance. No code, and none invented.
  }
  return [null, null];
}

/**
 * The sign-in redirect a 401 may start: at most one per tab session.
 *
 * platform-session 0.1.0 guards its own automatic redirect once per tab, but
 * its signIn() clears that guard, because it is meant for a button somebody
 * pressed. Called on every 401 it loops: when the API keeps refusing the token
 * Entra issued (a scope or tenant mismatch answers INVALID_TOKEN on every
 * call), each return from Entra fails again and redirects again, for ever.
 * And two queries refused together start two redirects, the second rejecting
 * with nobody listening. Found in review on 28 September 2026.
 *
 * So the redirect is remembered in sessionStorage, which survives the round
 * trip and ends with the tab, and forgotten after the first authenticated call
 * that succeeds. Where the tab cannot remember (storage refused), there is no
 * automatic redirect at all: one that cannot be counted cannot be bounded. A
 * redirect already in flight is joined rather than started twice.
 */
const REDIRECTED = "transport-planner:sign-in-redirected";

let redirecting: Promise<void> | null = null;

/** True, false, or null when the tab's storage cannot be used. */
function alreadyRedirected(): boolean | null {
  try {
    return window.sessionStorage.getItem(REDIRECTED) !== null;
  } catch {
    return null;
  }
}

function remember(redirected: boolean): boolean {
  try {
    if (redirected) window.sessionStorage.setItem(REDIRECTED, "1");
    else window.sessionStorage.removeItem(REDIRECTED);
    return true;
  } catch {
    return false;
  }
}

/** Starts, or joins, the one redirect this tab session may make. */
function redirectToSignIn(): boolean {
  if (redirecting) return true;
  if (alreadyRedirected() !== false || !remember(true)) return false;
  redirecting = session
    .signIn()
    .catch((error: unknown) => {
      // Logged, not rethrown: nothing awaits this, and the refusal that
      // started it is already on its way to the screen.
      console.error("The sign-in redirect could not start", error);
    })
    .finally(() => {
      redirecting = null;
    });
  return true;
}

/**
 * A sign-in the browser could not renew without the person: MSAL's silent
 * renewal in a hidden frame timed out, or Entra wants the person back. The
 * request never left the browser, so this is not the API failing, and a
 * screen says the sign-in has expired (`components/Failure.tsx`).
 *
 * `signingIn` is true when a redirect to the sign-in page is under way, and
 * false when this tab has already made its one automatic redirect: then the
 * screen offers a button instead of looping.
 */
export class SignInExpired extends Error {
  readonly signingIn: boolean;

  constructor(signingIn: boolean, cause: unknown) {
    super("The sign-in has expired", { cause });
    this.name = "SignInExpired";
    this.signingIn = signingIn;
  }
}

const errorCodeOf = (error: unknown): unknown =>
  error && typeof error === "object" ? (error as { errorCode?: unknown }).errorCode : undefined;

/**
 * What platform-session 0.1.0 already redirects on before it rethrows: any
 * InteractionRequiredAuthError, and these three codes (read from its dist on
 * 2 October 2026). A second redirect here would race that one.
 */
function sessionRedirected(error: unknown): boolean {
  const code = errorCodeOf(error);
  return (
    (error instanceof Error && error.name === "InteractionRequiredAuthError") ||
    code === InteractionRequiredAuthErrorCodes.interactionRequired ||
    code === InteractionRequiredAuthErrorCodes.loginRequired ||
    code === InteractionRequiredAuthErrorCodes.consentRequired
  );
}

/** The token, or `SignInExpired` when silent renewal cannot succeed. */
async function token(): Promise<string | null> {
  try {
    return await session.accessToken();
  } catch (error) {
    // A redirect is already under way: the session's own, or another call's,
    // which MSAL refuses to start twice (interaction_in_progress).
    if (sessionRedirected(error) || errorCodeOf(error) === BrowserAuthErrorCodes.interactionInProgress) {
      throw new SignInExpired(true, error);
    }
    // The hidden-frame renewal timed out: only a redirect renews it, and it
    // is bounded like every other automatic redirect, once per tab session.
    if (errorCodeOf(error) === BrowserAuthErrorCodes.timedOut) {
      throw new SignInExpired(redirectToSignIn(), error);
    }
    throw error;
  }
}

/** Asks for a sign-in because somebody pressed "Sign in again". */
export function signInAgain(): void {
  void session.signIn().catch((error: unknown) => {
    console.error("The sign-in redirect could not start", error);
  });
}

/** A write: the verb and the JSON body. Road closures are the only writes
 *  (spec D5); every other call is a GET. */
export interface Write {
  method: "POST" | "PATCH" | "PUT";
  body: unknown;
}

/** Exported for `closures.ts`, the one other module that calls the API, so
 *  the token, the envelope and the sign-in redirect stay in this one place.
 *  A screen calls `api` or `closuresApi`, never this. */
export async function request<T>(path: string, query: Query = {}, write?: Write): Promise<T> {
  if (fixtureMode) {
    const { answer } = await import("./fixtures");
    return answer(path, query, write) as T;
  }

  const url = new URL(path, window.location.origin);
  for (const [name, value] of Object.entries(query)) {
    if (value !== undefined) url.searchParams.set(name, String(value));
  }
  const bearer = await token();
  const headers: Record<string, string> = { Accept: "application/json" };
  if (bearer) headers.Authorization = `Bearer ${bearer}`;
  if (write) headers["Content-Type"] = "application/json";

  const response = await fetch(
    url.toString(),
    write ? { headers, method: write.method, body: JSON.stringify(write.body) } : { headers },
  );
  if (!response.ok) {
    // Read twice: once by the package for the sentence, once here for the
    // two fields it drops. A clone, because a body can be read only once.
    const [code, field] = await envelopeFields(response.clone());
    const base = await readError(response, path);
    // A 401 without a token, or NOT_AUTHENTICATED, is a sign-in that is
    // missing, and a redirect can supply it, once. A 401 on a token that was
    // sent (INVALID_TOKEN) is the API refusing a sign-in that happened, and
    // another redirect returns the same token. A 403 is a signed-in person
    // without the role, and a sign-in would change nothing.
    const signingIn =
      base.isUnauthenticated &&
      (bearer === null || code === "NOT_AUTHENTICATED") &&
      redirectToSignIn();
    throw new PlannerApiError(base, code, field, signingIn);
  }
  // The token was accepted, so the sign-in works: a later expiry may redirect
  // once more.
  if (bearer) remember(false);
  // 204 No Content (the cheese plan's write): there is no body to read.
  if (response.status === 204) return undefined as T;
  return (await response.json()) as T;
}

export const api = {
  runs: () => request<RunList>("/api/runs"),

  day: (runId: number, date: string) =>
    request<Day>(`/api/runs/${runId}/days/${encodeURIComponent(date)}`),

  week: (runId: number, monday: string) =>
    request<Week>(`/api/runs/${runId}/weeks/${encodeURIComponent(monday)}`),

  // Both bounds the same day for the day page. The API still checks the whole
  // horizon and only selects by the range, and it returns every undated
  // finding (a farm's gap, the whole run) whatever the range is.
  findings: (runId: number, from: string, to: string) =>
    request<Findings>(`/api/runs/${runId}/findings`, { from, to }),
};
